/**
 * WebAuthn utility functions for passkey integration
 * Handles browser WebAuthn API interactions
 */

// Check if WebAuthn is supported in the current browser
export const isWebAuthnSupported = (): boolean => {
  return !!(typeof navigator !== 'undefined' && 
           navigator.credentials && 
           typeof navigator.credentials.create === 'function' && 
           typeof navigator.credentials.get === 'function');
};

// Check if the device supports platform authenticators (Touch ID, Face ID, Windows Hello, etc.)
export const isPlatformAuthenticatorAvailable = async (): Promise<boolean> => {
  if (!isWebAuthnSupported()) {
    return false;
  }

  try {
    const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    return available;
  } catch (error) {
    console.warn('Failed to check platform authenticator availability:', error);
    return false;
  }
};

// Convert base64url to ArrayBuffer
export const base64urlToArrayBuffer = (base64url: string): ArrayBuffer => {
  // Add padding if needed
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;
  
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  
  return bytes.buffer;
};

// Convert ArrayBuffer to base64url
export const arrayBufferToBase64url = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

// Convert server credential creation options to browser format
export const prepareCredentialCreationOptions = (serverOptions: any): CredentialCreationOptions => {
  // The backend (go-webauthn) wraps the options under a `publicKey` key; accept either shape.
  const options = serverOptions?.publicKey ?? serverOptions;
  const publicKey: PublicKeyCredentialCreationOptions = {
    ...options,
    challenge: base64urlToArrayBuffer(options.challenge),
    user: {
      ...options.user,
      id: base64urlToArrayBuffer(options.user.id),
    },
    excludeCredentials: options.excludeCredentials?.map((cred: any) => ({
      ...cred,
      id: base64urlToArrayBuffer(cred.id),
    })) || [],
  };

  return { publicKey };
};

// Convert server credential request options to browser format
export const prepareCredentialRequestOptions = (serverOptions: any): CredentialRequestOptions => {
  // The backend (go-webauthn) wraps the options under a `publicKey` key; accept either shape.
  const options = serverOptions?.publicKey ?? serverOptions;
  const publicKey: PublicKeyCredentialRequestOptions = {
    ...options,
    challenge: base64urlToArrayBuffer(options.challenge),
    allowCredentials: options.allowCredentials?.map((cred: any) => ({
      ...cred,
      id: base64urlToArrayBuffer(cred.id),
    })) || [],
  };

  return { publicKey };
};

// Convert browser credential creation response to server format
export const formatCredentialCreationResponse = (credential: PublicKeyCredential): any => {
  const response = credential.response as AuthenticatorAttestationResponse;
  
  return {
    id: credential.id,
    rawId: arrayBufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: arrayBufferToBase64url(response.attestationObject),
      clientDataJSON: arrayBufferToBase64url(response.clientDataJSON),
    },
  };
};

// Convert browser credential assertion response to server format
export const formatCredentialAssertionResponse = (credential: PublicKeyCredential): any => {
  const response = credential.response as AuthenticatorAssertionResponse;
  
  return {
    id: credential.id,
    rawId: arrayBufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      authenticatorData: arrayBufferToBase64url(response.authenticatorData),
      clientDataJSON: arrayBufferToBase64url(response.clientDataJSON),
      signature: arrayBufferToBase64url(response.signature),
      userHandle: response.userHandle ? arrayBufferToBase64url(response.userHandle) : null,
    },
  };
};

// Create a new passkey credential
export const createPasskey = async (options: any): Promise<any> => {
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn is not supported in this browser');
  }

  try {
    const credentialOptions = prepareCredentialCreationOptions(options);
    const credential = await navigator.credentials.create(credentialOptions) as PublicKeyCredential;
    
    if (!credential) {
      throw new Error('Failed to create credential');
    }

    return formatCredentialCreationResponse(credential);
  } catch (error: any) {
    console.error('Passkey creation failed:', error);
    
    // Provide user-friendly error messages
    if (error.name === 'InvalidStateError') {
      throw new Error('A passkey for this account already exists on this device');
    } else if (error.name === 'NotSupportedError') {
      throw new Error('Passkeys are not supported on this device');
    } else if (error.name === 'SecurityError') {
      throw new Error('Security error - please try again');
    } else if (error.name === 'AbortError') {
      throw new Error('Passkey creation was cancelled');
    } else {
      throw new Error('Failed to create passkey. Please try again.');
    }
  }
};

// Authenticate with a passkey
export const authenticateWithPasskey = async (options: any): Promise<any> => {
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn is not supported in this browser');
  }

  try {
    const credentialOptions = prepareCredentialRequestOptions(options);
    const credential = await navigator.credentials.get(credentialOptions) as PublicKeyCredential;
    
    if (!credential) {
      throw new Error('Failed to authenticate with passkey');
    }

    return formatCredentialAssertionResponse(credential);
  } catch (error: any) {
    console.error('Passkey authentication failed:', error);
    
    // Provide user-friendly error messages
    if (error.name === 'InvalidStateError') {
      throw new Error('No passkey found for this account on this device');
    } else if (error.name === 'NotSupportedError') {
      throw new Error('Passkeys are not supported on this device');
    } else if (error.name === 'SecurityError') {
      throw new Error('Security error - please try again');
    } else if (error.name === 'AbortError') {
      throw new Error('Passkey authentication was cancelled');
    } else {
      throw new Error('Failed to authenticate with passkey. Please try again.');
    }
  }
};

// Get a user-friendly device name for the current browser/device
export const getDeviceName = (): string => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;
  
  // Basic device detection
  if (/iPhone|iPad|iPod/.test(userAgent)) {
    return /iPad/.test(userAgent) ? 'iPad' : 'iPhone';
  } else if (/Android/.test(userAgent)) {
    return 'Android Device';
  } else if (/Macintosh/.test(userAgent)) {
    return 'Mac';
  } else if (/Windows/.test(userAgent)) {
    return 'Windows PC';
  } else if (/Linux/.test(userAgent)) {
    return 'Linux Device';
  } else {
    // Fallback to browser name
    if (/Chrome/.test(userAgent)) {
      return 'Chrome Browser';
    } else if (/Firefox/.test(userAgent)) {
      return 'Firefox Browser';
    } else if (/Safari/.test(userAgent)) {
      return 'Safari Browser';
    } else if (/Edge/.test(userAgent)) {
      return 'Edge Browser';
    } else {
      return 'Unknown Device';
    }
  }
};

// Check if the current context is secure (HTTPS or localhost)
export const isSecureContext = (): boolean => {
  return window.isSecureContext;
};

// Comprehensive WebAuthn support check with detailed information
export const getWebAuthnSupport = async (): Promise<{
  isSupported: boolean;
  isSecureContext: boolean;
  isPlatformAuthenticatorAvailable: boolean;
  supportDetails: string;
}> => {
  const isSupported = isWebAuthnSupported();
  const isSecure = isSecureContext();
  const isPlatformAvailable = await isPlatformAuthenticatorAvailable();
  
  let supportDetails = '';
  
  if (!isSecure) {
    supportDetails = 'WebAuthn requires a secure context (HTTPS or localhost)';
  } else if (!isSupported) {
    supportDetails = 'WebAuthn is not supported in this browser';
  } else if (!isPlatformAvailable) {
    supportDetails = 'Platform authenticator not available - you can still use security keys';
  } else {
    supportDetails = 'Full WebAuthn support available';
  }
  
  return {
    isSupported,
    isSecureContext: isSecure,
    isPlatformAuthenticatorAvailable: isPlatformAvailable,
    supportDetails,
  };
};